import React, {Fragment, useEffect, useState} from 'react';
import styled from 'styled-components';
import {AstView} from 'rendition-core';
import {UninteractiveRegrouping} from './UninteractiveRegrouping';
import {Listbox, Transition} from '@headlessui/react';
import {loadFonts} from 'utils/loadFonts';

interface Props {
    documentId: string;
    save: () => void;
    view: AstView;
    setView: (view: AstView) => void;
}

export const PublishCore: React.FC<React.PropsWithChildren<Props>> = ({view, setView, save}) => {
    const [selectedResponsiveMode, setSelectedResponsiveMode] = useState<responsiveModeOptions>('Responsive');
    const {width} = useWindowSize();

    useEffect(() => {
        loadFonts(view);
    }, [view]);

    return (
        <>
            <Page>
                <div className="flex h-16 w-full items-center justify-end p-4">
                    <ResponsiveModeSelection
                        selected={selectedResponsiveMode}
                        setSelected={setSelectedResponsiveMode}
                    />
                </div>

                {selectedResponsiveMode === 'Responsive' && (
                    <UninteractiveRegrouping saveView={save} view={view} setView={setView} />
                )}

                {selectedResponsiveMode === 'Zoom' && (
                    <div
                        style={{
                            zoom: `${width / view.tree.position.width}`,
                            // TODO fix zoom on Firefox.
                            ['MozTransform' as any]: 'scale(' + width / view.tree.position.width + ')',
                            MozTransformOrigin: 'left top',
                        }}
                    >
                        <UninteractiveRegrouping saveView={save} view={view} setView={setView} />
                    </div>
                )}
            </Page>
        </>
    );
};

interface PublishProps extends React.PropsWithChildren {
    documentId: string;
    save: () => void;
    view: AstView;
    setView: (view: AstView) => void;
}

export const Publish: React.FC<PublishProps> = ({view, setView, save, documentId}) => {
    return <PublishCore documentId={documentId} save={save} view={view} setView={setView} />;
};

const Page = styled.div`
    display: flex;
    position: relative;
    justify-content: flex-start;
    align-items: center;
    flex-flow: column;
    min-height: 100vh;
    background-color: #000;
`;

export const PublishIcon = () => (
    <svg fill="none" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
        <g clipRule="evenodd" fill="#fff" fillRule="evenodd">
            <path d="m4.48449 12.3082c-.09008-.1112-.17158-.2145-.24407-.3082.07249-.0937.15399-.197.24407-.3082.43355-.5355 1.05942-1.2474 1.82903-1.95623 1.57495-1.45051 3.58645-2.73557 5.68648-2.73557 2.1001 0 4.1116 1.28506 5.6865 2.73557.7697.70883 1.3955 1.42073 1.8291 1.95623.09.1112.1716.2145.244.3082-.0724.0937-.154.197-.244.3082-.4336.5355-1.0594 1.2474-1.8291 1.9562-1.5749 1.4505-3.5864 2.7356-5.6865 2.7356-2.10003 0-4.11153-1.2851-5.68648-2.7356-.76961-.7088-1.39548-1.4207-1.82903-1.9562zm17.34531-.8662c.0002.0002.0004.0005-.8298.558.8302.5575.83.5578.8298.558l-.0004.0007-.0012.0018-.0035.0051-.0114.0167c-.0095.0139-.0229.0334-.0402.0581-.0345.0493-.0842.1195-.1485.2074-.1285.1758-.3154.4234-.5546.719-.4773.5895-1.1695 1.3776-2.0286 2.1688-1.6824 1.5495-4.1709 3.2644-7.0414 3.2644-2.87047 0-5.35897-1.7149-7.04138-3.2644-.85907-.7912-1.55122-1.5793-2.02852-2.1688-.23928-.2956-.42615-.5432-.55462-.719-.06427-.0879-.11403-.1581-.14853-.2074-.01725-.0247-.03069-.0442-.04024-.0581l-.01138-.0167-.00348-.0051-.00118-.0018-.00045-.0007c-.00018-.0002-.00036-.0005.82981-.558-.83017-.5575-.82999-.5578-.82981-.558l.00045-.0007.00118-.0018.00348-.0051.01138-.0167c.00955-.0139.02299-.0334.04024-.0581.0345-.0493.08426-.1195.14853-.2074.12847-.1758.31534-.4234.55462-.719.4773-.58948 1.16945-1.37757 2.02852-2.16877 1.68241-1.54949 4.17091-3.26443 7.04138-3.26443 2.8705 0 5.359 1.71494 7.0414 3.26443.8591.7912 1.5513 1.57929 2.0286 2.16877.2392.2956.4261.5432.5546.719.0643.0879.114.1581.1485.2074.0173.0247.0307.0442.0402.0581l.0114.0167.0035.0051.0012.0018zm-.8298.558.8302-.5575c.2264.3372.2264.7778 0 1.115zm-18.83014-.5575.83017.5575-.83017.5575c-.22644-.3372-.22644-.7778 0-1.115zm8.83014.5575c0-.5523.4477-1 1-1s1 .4477 1 1-.4477 1-1 1-1-.4477-1-1zm1-3c-1.6568 0-2.99997 1.3431-2.99997 3s1.34317 3 2.99997 3c1.6569 0 3-1.3431 3-3s-1.3431-3-3-3z" />
            <path
                d="m3 12c1.33333-2 5-6 9-6s7.6667 4 9 6c-1.6667 2-5 6-9 6s-7.33333-4-9-6zm9 2c1.1046 0 2-.8954 2-2s-.8954-2-2-2-2 .8954-2 2 .8954 2 2 2z"
                fillOpacity=".1"
            />
        </g>
    </svg>
);

const responsiveModeArray = ['Responsive', 'Zoom'];
type responsiveModeOptions = 'Responsive' | 'Zoom';

type ResponsiveModeSelectionProps = {
    selected: responsiveModeOptions;
    setSelected: (arg0: responsiveModeOptions) => void;
};

const ResponsiveModeSelection: React.FC<ResponsiveModeSelectionProps> = ({selected, setSelected}) => {
    return (
        <div className="w-40 text-black">
            <Listbox value={selected} onChange={setSelected}>
                <div className="relative mt-1">
                    <Listbox.Button className="relative w-full cursor-default rounded-lg bg-white py-2 pl-3 pr-10 text-left shadow-md focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 sm:text-sm">
                        <span className="block truncate">{selected}</span>
                        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2"></span>
                    </Listbox.Button>
                    <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Listbox.Options className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                            {responsiveModeArray.map((person, personIdx) => (
                                <Listbox.Option
                                    key={personIdx}
                                    className={({active}) =>
                                        `relative cursor-default select-none py-2 pl-10 pr-4 ${
                                            active ? 'bg-amber-100 text-amber-900' : 'text-gray-900'
                                        }`
                                    }
                                    value={person}
                                >
                                    {({selected}) => (
                                        <>
                                            <span
                                                className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}
                                            >
                                                {person}
                                            </span>
                                            {selected ? (
                                                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                                                    ✅
                                                </span>
                                            ) : null}
                                        </>
                                    )}
                                </Listbox.Option>
                            ))}
                        </Listbox.Options>
                    </Transition>
                </div>
            </Listbox>
        </div>
    );
};

// Define general type for useWindowSize hook, which includes width and height
interface Size {
    width: number;
    height: number;
}

// Hook
function useWindowSize(): Size {
    // Initialize state with undefined width/height so server and client renders match
    // Learn more here: https://joshwcomeau.com/react/the-perils-of-rehydration/
    const [windowSize, setWindowSize] = useState<Size>({
        width: 1,
        height: 1,
    });
    useEffect(() => {
        // Handler to call on window resize
        function handleResize() {
            // Set window width/height to state
            setWindowSize({
                width: window.outerWidth,
                height: window.outerHeight,
            });
        }
        // Add event listener
        window.addEventListener('resize', handleResize);
        // Call handler right away so state gets updated with initial window size
        handleResize();
        // Remove event listener on cleanup
        return () => window.removeEventListener('resize', handleResize);
    }, []); // Empty array ensures that effect is only run on mount
    return windowSize;
}
